import { useEffect, useState, useCallback } from "react";

const APP_VERSION_KEY = "app_build_version";
const VERSION_CHECK_URL = "/version.json";

/*
 * НЕ ЧАЩЕ РАЗА В ПЯТЬ МИНУТ — И ТАЙМЕР ОДИН НА МОДУЛЬ, А НЕ НА ЭКЗЕМПЛЯР ХУКА.
 *
 * Проверка висит на `visibilitychange` и на `focus`, а `focus` срабатывает на
 * каждый клик по окну после DevTools, клавиатуры iPad, системного диалога.
 * Журнал 27.09.2026: пять запросов `version.json` за 45 секунд, три — за две
 * секунды подряд. Возврат на вкладку к тому же даёт оба события сразу, а
 * StrictMode в dev монтирует хук дважды. Общая отметка времени сливает всё это
 * в одну проверку. Выкатки редки: узнать о новой версии через пять минут после
 * возвращения на вкладку, а не мгновенно, ничего не стоит.
 */
const MIN_CHECK_INTERVAL_MS = 5 * 60 * 1000;
let lastCheckAt = 0;

interface VersionInfo {
  version: string;
  buildTime: string;
}

interface UseVersionCheckResult {
  hasUpdate: boolean;
  currentVersion: string | null;
  newVersion: string | null;
  dismissUpdate: () => void;
  applyUpdate: () => void;
}

/**
 * Хук для проверки версии приложения при возвращении на вкладку.
 * Показывает уведомление если вышла новая версия после деплоя.
 */
export function useVersionCheck(): UseVersionCheckResult {
  const [hasUpdate, setHasUpdate] = useState(false);
  const [currentVersion, setCurrentVersion] = useState<string | null>(null);
  const [newVersion, setNewVersion] = useState<string | null>(null);

  const checkVersion = useCallback(async () => {
    const now = Date.now();
    if (now - lastCheckAt < MIN_CHECK_INTERVAL_MS) return;
    lastCheckAt = now;
    try {
      // Добавляем timestamp чтобы избежать кэширования
      const response = await fetch(`${VERSION_CHECK_URL}?t=${Date.now()}`, {
        cache: "no-store",
      });

      if (!response.ok) {
        console.warn("Version check: failed to fetch version.json");
        return;
      }

      const data: VersionInfo = await response.json();
      const storedVersion = localStorage.getItem(APP_VERSION_KEY);

      // Первый запуск - просто сохраняем версию
      if (!storedVersion) {
        localStorage.setItem(APP_VERSION_KEY, data.version);
        setCurrentVersion(data.version);
        return;
      }

      setCurrentVersion(storedVersion);

      // Если версия изменилась - показываем уведомление
      if (storedVersion !== data.version) {
        setNewVersion(data.version);
        setHasUpdate(true);
      }
    } catch (error) {
      // Молча игнорируем ошибки (нет сети, файл не найден и т.д.)
      console.warn("Version check failed:", error);
    }
  }, []);

  // Проверяем версию при возвращении на вкладку
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkVersion();
      }
    };

    // Проверяем при первом рендере
    checkVersion();

    // Слушаем переключение вкладок
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Также проверяем при получении фокуса (для мобильных)
    window.addEventListener("focus", checkVersion);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", checkVersion);
    };
  }, [checkVersion]);

  // Отклонить обновление (скрыть уведомление)
  const dismissUpdate = useCallback(() => {
    setHasUpdate(false);
    // Можно добавить временное скрытие на N минут
    // sessionStorage.setItem('update_dismissed', Date.now().toString());
  }, []);

  // Применить обновление (перезагрузить страницу)
  const applyUpdate = useCallback(() => {
    if (newVersion) {
      localStorage.setItem(APP_VERSION_KEY, newVersion);
    }
    window.location.reload();
  }, [newVersion]);

  return {
    hasUpdate,
    currentVersion,
    newVersion,
    dismissUpdate,
    applyUpdate,
  };
}
